import React, { useContext, useState, FunctionComponent } from 'react';
import { useTranslation } from 'react-i18next';

// MUI
import Alert from '@mui/material/Alert';
import Chip, { ChipProps } from '@mui/material/Chip';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Toolbar from '@mui/material/Toolbar';

// Custom Components
import DraggableDialog from '@/ui/Dialog/DraggableDialog';
import DialogToolbarHeading from '@/ui/Dialog/DialogToolbarHeading';
import DialogToolbarButtonClose from '@/ui/Dialog/ToolbarButtons/DialogToolbarButtonClose';
import DialogActionButtonCancel from '@/ui/Dialog/ActionButtons/DialogActionButtonCancel';
import DialogActionButtonSave from '@/ui/Dialog/ActionButtons/DialogActionButtonSave';
import ToolbarFillContent from '@/ui/Toolbar/ToolbarFillContent';
import DialogContext, { IDialogProps } from '@/context/DialogContext/DialogContext';
import SnackbarContext, { SnackbarContextType } from '@/context/SnackbarContext/SnackbarContext';

// Models
import { upitnikOdgovorStatusItems, UpitnikOdgovorStatus } from '@/models/groblja/upitnik_odgovori';

// Services
import { upitnikService } from '@/services/upitnikService';

const UpitnikOdgovoriStatusDialog: FunctionComponent<IDialogProps> = (props) => {
  const { t } = useTranslation();
  const { hideDialog } = useContext(DialogContext);
  const snackbarContext = useContext(SnackbarContext) as SnackbarContextType;

  const { ids, currentStatus, onSaved } = props as {
    ids: number[];
    currentStatus: UpitnikOdgovorStatus | null;
    onSaved: () => void;
  };

  // preselected only when the whole selection shares one status
  const [status, setStatus] = useState<UpitnikOdgovorStatus | null>(currentStatus);
  const [saving, setSaving] = useState(false);

  const handleClose = () => {
    if (!saving) hideDialog();
  };

  const handleSave = async () => {
    if (!status) return;
    setSaving(true);
    try {
      const resp = await upitnikService.updateOdgovoriStatus(ids, status);
      if (!resp.success) throw new Error('updateOdgovoriStatus');
      const count = (resp.data as { count?: number } | undefined)?.count ?? ids.length;
      snackbarContext.showNotification(t('groblja:upitnik_odgovori.status_promijenjen', { count }), 'success');
      hideDialog();
      onSaved();
    } catch {
      snackbarContext.showNotification(t('groblja:upitnik_odgovori.status_greska'), 'error');
      setSaving(false);
    }
  };

  return (
    <DraggableDialog open maxWidth="xs" minHeight="auto" onClose={handleClose}>
      <DialogTitle>
        <Toolbar variant="dense" disableGutters>
          <DialogToolbarHeading>{t('groblja:upitnik_odgovori.promijeni_status')}</DialogToolbarHeading>
          <ToolbarFillContent />
          <DialogToolbarButtonClose onClick={handleClose} />
        </Toolbar>
      </DialogTitle>
      <DialogContent>
        <Alert severity="warning" sx={{ mb: 2 }}>
          {t('groblja:upitnik_odgovori.promijeni_status_upozorenje', { count: ids.length })}
        </Alert>
        <RadioGroup value={status ?? ''} onChange={(evt) => setStatus(evt.target.value as UpitnikOdgovorStatus)}>
          {upitnikOdgovorStatusItems.values.map((value, i) => (
            <FormControlLabel
              key={value}
              value={value}
              disabled={saving}
              control={<Radio />}
              label={
                <Chip
                  size="small"
                  label={t(upitnikOdgovorStatusItems.labels[i])}
                  color={upitnikOdgovorStatusItems.chipColors[i] as ChipProps['color']}
                  sx={{ cursor: 'pointer' }}
                />
              }
              sx={{ my: 0.25 }}
            />
          ))}
        </RadioGroup>
      </DialogContent>
      <DialogActions>
        <DialogActionButtonCancel variant="outlined" onClick={handleClose} disabled={saving} />
        <DialogActionButtonSave variant="contained" onClick={handleSave} disabled={!status || saving} />
      </DialogActions>
    </DraggableDialog>
  );
};

export default UpitnikOdgovoriStatusDialog;
